import React, { useRef, useState } from 'react';
import { Box, Slide, Typography, TypographyProps, colors, styled, Grid } from '@mui/material';
import { Stack, SxProps, useMediaQuery } from '@mui/system';
import { ChangeHistory, CurrencyExchange } from '@mui/icons-material';

import { MIT, MITStatus, MITEventType, MITEvent } from '../../../../store/modules/mit/types';
import appTheme from '../../../../styles/themes/theme';
import { useGetPublicMerchantQuery } from '../../../../store/modules/merchant/api';
import { MITSummary } from '../MITSummary';
import { AcceptedCurrency } from '../../../../store/modules/merchant/types';
import { RefundMIT } from '../RefundMIT';
import { useRefundMIT } from '../../hooks/useRefundMIT';
import { CustomerSummary } from '../CustomerSummary';
import { SectionDescription } from './SectionDescription';
import { Button } from '../../../../components/core/Button/Button';

type StyledTypography = React.ComponentType<TypographyProps<'span', { component?: 'span' }>>;
const SectionTitle: StyledTypography = styled(Typography)({
  fontSize: 12,
  color: 'grey.600',
  fontWeight: 600,
});

const SlideWrapper = styled(Box)({
  width: `100%`,
  height: 'calc(100% - 2px)',
  position: 'absolute',
  backgroundColor: 'white',
  borderRadius: '8px',
  top: 0,
  left: 0,
});

export type MITDetailsPage = 'details' | 'refund';

interface Props {
  merchantId: string;
  mit: MIT;
  defaultOpenPage?: MITDetailsPage;
  slideWrapperStyles?: SxProps;
}

export const MITDetails = ({
  merchantId,
  mit,
  slideWrapperStyles,
  defaultOpenPage = 'details',
}: Props) => {
  const containerRef = useRef<HTMLElement>(null);
  const [isRefunding, setIsRefunding] = useState(defaultOpenPage === 'refund');
  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'));

  const { data: publicData } = useGetPublicMerchantQuery(merchantId, { skip: !merchantId });
  const currency = publicData?.acceptedCurrency;

  const { onRefundMIT, isLoading: isLoadingRefund } = useRefundMIT({
    mitId: mit.mitId,
  });

  const { status, description, merchantReference, paymentMethod, customer } = mit;

  const renderMitEvent = (event: MITEvent) => {
    let text = '';
    let icon: JSX.Element | null = null;

    if (event.eventType === MITEventType.CREATED) {
      text = `Transaction has been created by ${event.createdBy}`;
      icon = <ChangeHistory />;
    }
    if (event.eventType === MITEventType.REFUNDED) {
      text = `Refund has been issued by ${event.createdBy}`;
      icon = <CurrencyExchange />;
    }

    return (
      <SectionDescription sx={{ lineBreak: 'anywhere' }} startIcon={icon} key={event.eventId}>
        {text}
      </SectionDescription>
    );
  };

  const canRefund = status === MITStatus.PAID || status === MITStatus.PARTIALLY_REFUNDED;

  return (
    <Box sx={{ height: '100%', position: 'relative' }}>
      <Box
        ref={containerRef}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          py: 2,
          px: 3,
          height: '100%',
        }}
      >
        <Stack spacing={isMobile ? 1.5 : 4} sx={{ mb: '47px' }}>
          <MITSummary mit={mit} currency={currency as AcceptedCurrency} />

          <CustomerSummary customer={customer} paymentMethod={paymentMethod} />

          <Grid container rowGap={1.5}>
            <Grid item xs={12} md={6}>
              <SectionTitle>Description</SectionTitle>
              <SectionDescription>{description || 'N/A'}</SectionDescription>
            </Grid>
            {merchantReference && (
              <Grid item xs={12} md={6}>
                <SectionTitle>Merchant Reference</SectionTitle>
                <SectionDescription>{merchantReference}</SectionDescription>
              </Grid>
            )}
          </Grid>

          <Box>
            <SectionTitle>Transaction ID</SectionTitle>
            <SectionDescription>{mit.mitId}</SectionDescription>
          </Box>

          <Box>
            <SectionTitle>{`History (${mit.mitEvents?.length || 0})`}</SectionTitle>
            {mit.mitEvents
              ?.toSorted(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
              )
              ?.map(renderMitEvent)}
          </Box>
        </Stack>

        {canRefund && (
          <Box
            sx={{
              borderTop: `1px solid ${colors.grey[400]}`,
              paddingTop: 2,
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              '& button': {
                color: colors.grey[600],
                fontSize: 14,
                lineHeight: '18px',
                fontWeight: 700,
                textTransform: 'none',

                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }}
          >
            <Button
              variant="text"
              onClick={() => setIsRefunding(true)}
              sx={{ '& > *': { color: 'link.main' } }}
              dataTestId="mit-details-refund-button"
              text="Refund"
            />
          </Box>
        )}
      </Box>

      <Slide
        in={isRefunding}
        direction="left"
        container={containerRef.current}
        mountOnEnter
        unmountOnExit
      >
        <SlideWrapper sx={slideWrapperStyles}>
          <RefundMIT
            mit={mit}
            onClose={() => setIsRefunding(false)}
            onSubmit={onRefundMIT}
            isLoadingSubmit={isLoadingRefund}
            currency={currency}
          />
        </SlideWrapper>
      </Slide>
    </Box>
  );
};
